import React, { useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import { UPDATE_CONTACTS_MUTATION } from 'Queries/Mutations';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { CustomModal } from 'components/CustomModal';
import { ErrorBanner } from 'components/ErrorBanner/ErrorBanner';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { InputField } from 'components/FormHelper/InputField';

import { mobileOrPhoneOptional } from 'global/renderingValidationConstants';

import { YPhoneNumber } from 'utils/YupUtils';

import './Popup.sass';

export const DebInstallerPhonePopup = ({ mainFormik, popupOpen, setPopupOpen, onSaved }) => {
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const currentMobile = mainFormik.values.mobile || '';
  const currentPhone = mainFormik.values.phone || '';

  const validate = useMemo(() => {
    return (values) => {
      const errors = {};

      try {
        if (values.mobile) YPhoneNumber.validateSync(values.mobile);
      } catch (e) {
        errors.mobile = e.message || 'Ungültige Mobilnummer';
      }

      try {
        if (values.phone) YPhoneNumber.validateSync(values.phone);
      } catch (e) {
        errors.phone = e.message || 'Ungültige Telefonnummer';
      }

      const hasMobile = !!(values && values.mobile && String(values.mobile).trim());
      const hasPhone = !!(values && values.phone && String(values.phone).trim());

      const mobileOptional = mobileOrPhoneOptional('mobile').evaluate({ values });
      const phoneOptional = mobileOrPhoneOptional('phone').evaluate({ values });

      if (!hasMobile && !hasPhone && !mobileOptional && !phoneOptional) {
        const msg = 'Bitte mindestens Mobil- oder Telefonnummer angeben';
        errors.mobile = msg;
        errors.phone = msg;
      }

      return errors;
    };
  }, [currentMobile, currentPhone]);

  const formik = useFormik({
    validate,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      mobile: currentMobile,
      phone: currentPhone,
    },
  });

  const [updateContactsMutation, { loading: isSaving, error: mutationError }] =
    useMutation(UPDATE_CONTACTS_MUTATION);

  useEffect(() => {
    if (!popupOpen) return;
    formik.setValues({
      mobile: mainFormik.values.mobile || '',
      phone: mainFormik.values.phone || '',
    });
  }, [popupOpen]);

  const hasChanged = formik.values.mobile !== currentMobile || formik.values.phone !== currentPhone;

  const onCancel = () => {
    if (updateSuccess) {
      mainFormik.setFieldValue('mobile', formik.values.mobile, false);
      mainFormik.setFieldValue('phone', formik.values.phone, false);
      onSaved?.({ mobile: formik.values.mobile, phone: formik.values.phone });
    }
    formik.resetForm();
    setUpdateSuccess(false);
    setPopupOpen(false);
  };

  const onSubmit = () => {
    if (!formik.isValid || !hasChanged) return;

    updateContactsMutation({
      variables: {
        input: {
          telefonMobil: formik.values.mobile || null,
          telefonGeschaeft: formik.values.phone || null,
        },
      },
    })
      .then(() => {
        setUpdateSuccess(true);
      })
      .catch(() => {});
  };

  const modalContent = () => {
    if (updateSuccess) {
      return (
        <div className="popup-content">
          Vielen Dank. Ihre Kontaktdaten wurden gespeichert. <br />
        </div>
      );
    }
    if (mutationError) {
      return (
        <div className="popup-content">
          <ErrorBanner>
            Beim Aktualisieren der Daten ist ein Fehler aufgetreten. Bitte versuchen Sie es später
            erneut.
          </ErrorBanner>
        </div>
      );
    }

    return (
      <div className="popup-content">
        <div className="popup-content__popup-field">
          <InputField
            label="Telefon"
            name="phone"
            id="phone"
            value={formik.values.phone}
            fullWidth
            oldValue={currentPhone}
            onChange={formik.handleChange}
            error={Boolean(formik.errors?.phone)}
            helperText={formik.errors.phone || ''}
          />
        </div>
        <div className="popup-content__popup-field">
          <InputField
            label="Mobil"
            name="mobile"
            id="mobile"
            value={formik.values.mobile}
            fullWidth
            oldValue={currentMobile}
            onChange={formik.handleChange}
            error={Boolean(formik.errors?.mobile)}
            helperText={formik.errors.mobile || ''}
          />
        </div>
      </div>
    );
  };

  const modalButtons = () => {
    if (updateSuccess || mutationError) {
      return <ActionButton inverted onClick={onCancel} title="OK" />;
    }

    return (
      <FormActionButtons
        hasErrors={Object.keys(formik.errors).length > 0 || !hasChanged}
        titleLeft="Abbrechen"
        titleRight="Speichern"
        onClickLeft={onCancel}
        onClickRight={onSubmit}
        loading={isSaving}
        errorMessage={formik.errors.mobile || formik.errors.phone}
      />
    );
  };

  return (
    <CustomModal
      isOpen={popupOpen}
      title="Telefonnummern bearbeiten"
      content={modalContent()}
      closeCancelEventModal={onCancel}
      ctaButtons={modalButtons()}
    />
  );
};

DebInstallerPhonePopup.displayName = 'DebInstallerPhonePopup';

DebInstallerPhonePopup.propTypes = {
  mainFormik: PropTypes.object.isRequired,
  popupOpen: PropTypes.bool,
  setPopupOpen: PropTypes.func,
  onSaved: PropTypes.func,
};

DebInstallerPhonePopup.defaultProps = {
  popupOpen: false,
  setPopupOpen: () => {},
  onSaved: undefined,
};
